import { useEffect, useRef } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAuthToken } from "@convex-dev/auth/react";
import { useMutation, useQuery } from "convex/react";
import { AppWindow, ArrowLeft } from "lucide-react";

import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { STUDIO_CHAT_STARTERS } from "@/components/apps/studioChatStarters";
import { ChatWindow } from "@/components/chat/ChatWindow";
import { useStudioApps } from "@/hooks/useStudioApps";
import { logMutationError } from "@/lib/logger";
import { STUDIO_NAME } from "@domain/studio-app";

export const Route = createFileRoute("/_authenticated/studio/new/$threadId")({
  component: StudioGestationRoute,
  validateSearch: (search: Record<string, unknown>): { seed?: string } => {
    // Optional composer prefill from a deep link — pre-filled ONCE for the
    // user to review + send manually, never auto-sent (same contract as the
    // workflows gestation route's `seed`).
    return typeof search.seed === "string" ? { seed: search.seed } : {};
  },
});

/**
 * The new-app gestation route — the ONLY thread-keyed Studio URL (studio
 * spec §3.4). An app's identity is its vault directory (path-as-identity,
 * R3), which doesn't exist until the agent writes the first files — so a
 * fresh build can only be addressed by its thread. Chat-first: the user
 * describes the app (STUDIO_CHAT_STARTERS); on each assistant turn end the
 * owner list is re-read and the FIRST appDir absent from the mount-time
 * snapshot is stamped onto the thread (`threads.stampStudioApp`). The route
 * watches the thread's `metadata.appDir` reactively (covers stamps from
 * another tab too) and replace-redirects to `/studio/$appDir` the moment it
 * lands. Concurrent-write misattribution is an accepted benign race (v1).
 */
function StudioGestationRoute() {
  const { threadId } = Route.useParams();
  const { seed } = Route.useSearch();
  const navigate = useNavigate();
  const authToken = useAuthToken();
  const thread = useQuery(api.threads.get, {
    threadId: threadId as Id<"threads">,
  });
  const { apps, isLoading, refresh } = useStudioApps();
  const stampStudioApp = useMutation(api.threads.stampStudioApp);

  // Mount-time snapshot of the vault's appDirs — captured on the first
  // completed list load; the scan detects the first dir that wasn't there.
  const snapshotRef = useRef<ReadonlySet<string> | null>(null);
  useEffect(() => {
    if (isLoading || snapshotRef.current !== null) return;
    snapshotRef.current = new Set(apps.map((a) => a.appDir));
  }, [isLoading, apps]);

  // Turn-end scan → stamp (§3.4). Runs off the refreshed list; a failed
  // stamp re-arms so the next turn end retries.
  const stampedRef = useRef(false);
  useEffect(() => {
    const snapshot = snapshotRef.current;
    if (stampedRef.current || snapshot === null) return;
    const fresh = apps.find((a) => !snapshot.has(a.appDir));
    if (fresh === undefined) return;
    stampedRef.current = true;
    void stampStudioApp({
      threadId: threadId as Id<"threads">,
      appDir: fresh.appDir,
    }).catch((err) => {
      stampedRef.current = false;
      logMutationError({
        mutation: "threads.stampStudioApp",
        args: { appDir: fresh.appDir },
        error: err,
      });
    });
  }, [apps, stampStudioApp, threadId]);

  // The stamp landed (this tab or another) → the app has an identity; hand
  // off to the canonical app-keyed route.
  const stampedAppDir = thread?.metadata?.appDir;
  useEffect(() => {
    if (stampedAppDir === undefined) return;
    void navigate({
      to: "/studio/$appDir",
      params: { appDir: stampedAppDir },
      replace: true,
    });
  }, [stampedAppDir, navigate]);

  // Strip the one-shot `seed` param once ChatWindow has captured it into its
  // draft state (consume-then-strip, frontend-doctrine §4.11).
  useEffect(() => {
    if (seed === undefined) return;
    void navigate({
      to: "/studio/new/$threadId",
      params: { threadId },
      search: {},
      replace: true,
    });
  }, [seed, threadId, navigate]);

  return (
    <div className="flex h-[calc(100vh-10rem)] min-h-[480px] flex-col">
      <div className="mb-4 flex items-center gap-3">
        <button
          type="button"
          onClick={() => void navigate({ to: "/studio" })}
          className="inline-flex min-h-[38px] items-center gap-1.5 rounded-full border border-home-ink/15 px-4 font-body text-[13px] font-semibold text-home-ink/70 transition-colors hover:bg-black/5 dark:border-white/15 dark:text-white/70 dark:hover:bg-white/10"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={2.2} />
          {STUDIO_NAME}
        </button>
        <h2 className="min-w-0 truncate font-display text-[18px] font-semibold tracking-tight text-home-ink dark:text-white">
          New app
        </h2>
      </div>

      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(320px,420px)_1fr]">
        {/* Build chat — the primary pane (chat-first) */}
        <div className="flex min-h-0 flex-col overflow-hidden rounded-3xl border border-home-ink/8 bg-white dark:border-white/8 dark:bg-surface-dark-card">
          <ChatWindow
            className="flex-1 overflow-hidden"
            threadId={threadId as Id<"threads">}
            authToken={authToken}
            initialDraft={seed}
            starterSet={STUDIO_CHAT_STARTERS}
            onAssistantTurnEnd={() => void refresh()}
          />
        </div>

        {/* Ghost preview — same visual language as StudioPending */}
        <div className="hidden min-h-0 flex-col items-center justify-center rounded-3xl border border-dashed border-home-ink/15 lg:flex dark:border-white/15">
          <div className="relative rotate-[-2deg] opacity-70" aria-hidden>
            <div className="w-[220px] overflow-hidden rounded-2xl border border-dashed border-home-ink/20 bg-white/60 dark:border-white/20 dark:bg-white/[0.04]">
              <div className="h-20 border-b border-dashed border-home-ink/15 bg-home-ink/[0.04] dark:border-white/15 dark:bg-white/[0.06]" />
              <div className="flex items-center justify-between p-3">
                <span className="h-2 w-20 rounded-full bg-home-ink/15 dark:bg-white/15" />
                <span className="h-3.5 w-10 rounded-full bg-home-ink/[0.08] dark:bg-white/10" />
              </div>
            </div>
            <span className="absolute -top-3 -right-7 flex h-9 w-9 rotate-[8deg] items-center justify-center rounded-2xl bg-home-yellow shadow-[0_8px_20px_-8px_rgba(33,31,28,0.45)]">
              <AppWindow className="h-4.5 w-4.5 text-home-ink" strokeWidth={2.2} />
            </span>
          </div>
          <p className="mt-8 max-w-xs px-6 text-center font-body text-[14px] leading-relaxed text-home-ink/60 dark:text-white/60">
            Describe the app you want. It appears here the moment your agent
            writes its first files.
          </p>
        </div>
      </div>
    </div>
  );
}
